import { useMemo } from 'react'
import styles from './RoundTemplate.module.scss'
import commonStyles from '../../TableCardsManagement.module.scss'
import TableCardQrCode from '../../components/TableCardQrCode/TableCardQrCode'
import NfcIcon from '../assets/icons/nfc.svg?react'

interface RoundTemplateProps {
  withNfc?: boolean
  tableNumber: number
  qrCodeValue?: string
  languageCode?: 'en' | 'pl'
  logoImageUrl: string
  backgroundColorValue?: string
  contentColor?: string
  contrastColor?: string
  backgroundImage?: string | null
  width: number
  height: number
}

const BASE_TEMPLATE_WIDTH = 523
const BASE_TEMPLATE_HEIGHT = 523
const BASE_ASPECT_RATIO = BASE_TEMPLATE_WIDTH / BASE_TEMPLATE_HEIGHT
const ASPECT_RATIO_TOLERANCE = 0.1

function RoundTemplate(props: RoundTemplateProps) {
  const {
    withNfc,
    tableNumber,
    qrCodeValue,
    languageCode = 'en',
    logoImageUrl,
    backgroundColorValue,
    contentColor,
    contrastColor,
    backgroundImage,
    width,
    height
  } = props

  const {
    templateScale,
    offsetLeft,
    offsetTop,
    isAspectRatioValid,
    aspectRatioDeviationPercent
  } = useMemo(() => {
    const fitScale =
      Math.min(width / BASE_TEMPLATE_WIDTH, height / BASE_TEMPLATE_HEIGHT) || 0

    const computedOffsetLeft =
      (width - BASE_TEMPLATE_WIDTH * fitScale) / 2 / fitScale
    const computedOffsetTop =
      (height - BASE_TEMPLATE_HEIGHT * fitScale) / 2 / fitScale

    const userAspectRatio =
      width > 0 && height > 0 ? width / height : BASE_ASPECT_RATIO
    const relativeDeviation =
      Math.abs(userAspectRatio - BASE_ASPECT_RATIO) / BASE_ASPECT_RATIO

    return {
      templateScale: fitScale,
      offsetLeft: computedOffsetLeft,
      offsetTop: computedOffsetTop,
      isAspectRatioValid: relativeDeviation <= ASPECT_RATIO_TOLERANCE,
      aspectRatioDeviationPercent: relativeDeviation * 100
    }
  }, [width, height])

  return (
    <div
      className={styles.roundTemplateWrapper}
      style={{
        position: 'relative',
        width: `${width}px`,
        height: `${height}px`,
        backgroundColor: backgroundColorValue,
        backgroundImage: backgroundImage
          ? `url(${backgroundImage})`
          : undefined,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        borderRadius: '50%',
        overflow: 'hidden'
      }}
      data-has-aspect-ratio-warning={isAspectRatioValid ? undefined : 'true'}
      title={
        isAspectRatioValid
          ? undefined
          : `Size deviates ${aspectRatioDeviationPercent.toFixed(1)}% from 1:1`
      }
      aria-invalid={isAspectRatioValid ? undefined : true}
    >
      <div
        className={`${commonStyles.contrastOverlay} ${styles.contrastRoundOverlay}`}
        style={{ backgroundColor: contrastColor, borderRadius: '50%' }}
      />
      <div
        className={styles.roundContentWrapper}
        style={{
          position: 'absolute',
          left: `${offsetLeft}px`,
          top: `${offsetTop}px`,
          width: `${BASE_TEMPLATE_WIDTH}px`,
          height: `${BASE_TEMPLATE_HEIGHT}px`,
          zoom: templateScale
        }}
      >
        <div className={styles.templateContent}>
          <div className={styles.templateHeader}>
            <p
              className={`${styles.templateHeaderDescription} display-small`}
              style={{ color: contentColor }}
            >
              {languageCode === 'pl' ? 'Menu' : 'Scan'}
            </p>
            <p
              className={`${styles.templateHeaderDescription} display-small`}
              style={{ color: contentColor }}
            >
              {languageCode === 'pl' ? 'i płatność' : 'and pay now'}
            </p>
          </div>
          <div className={styles.templateQrCodeAndDescriptionWrapper}>
            <p
              className={`${styles.templateDescription} title-large`}
              style={{ color: contentColor }}
            >
              {languageCode === 'pl' ? 'Bez pobierania,' : 'No downloads,'}
            </p>
            <p
              className={`${styles.templateDescription} title-large`}
              style={{ color: contentColor }}
            >
              {languageCode === 'pl'
                ? 'bez rejestracji'
                : 'no registration required'}
            </p>
            <div
              className={styles.qrCodeImageWrapper}
              style={{
                backgroundColor: contentColor
              }}
            >
              <TableCardQrCode
                className={styles.qrCodeImage}
                value={qrCodeValue}
              />
            </div>
            <div className={styles.templateDescriptionWithMenu}>
              <p
                className={`${styles.templateDescriptionText} title-large`}
                style={{ color: contentColor }}
              >
                {languageCode === 'pl'
                  ? 'Zapłać bezpiecznie z'
                  : 'Pay securely with'}
              </p>
              <p
                className={`${styles.templateMenuText} logo-font-template-small`}
                style={{ color: contentColor }}
              >
                MENU!
              </p>
            </div>
          </div>
          <div
            className={styles.templateFooter}
            style={{ justifyContent: withNfc ? 'space-between' : 'center' }}
          >
            <div className={styles.templateLogoAndTabeNumberWrapper}>
              <img className={styles.logoImage} src={logoImageUrl} alt="Logo" />
              <p
                className={`${styles.tableNumber} ${
                  withNfc ? styles.tableNumberWithNFC : ''
                } display-medium`}
                style={{ color: contentColor }}
              >
                {tableNumber}
              </p>
            </div>
            {withNfc && (
              <div className={styles.nfcIconWrapper}>
                <NfcIcon
                  className={styles.nfcIcon}
                  style={{ color: contentColor }}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

export default RoundTemplate
